/**
 * Turns an API error response into something safe to show a user.
 *
 * Report endpoints can fail with raw driver/SQL errors in `detail` (a full
 * SELECT statement, "pyodbc.OperationalError", "mysql.connector...", a missing
 * table, an unseeded query template). Rendering that verbatim looks broken and
 * leaks schema details, so anything that smells technical is swapped for a
 * plain-English message. Genuinely user-facing messages are passed through.
 */

const TECHNICAL_MARKERS = [
  'select ',
  'from [',
  'where [',
  ' from ',
  'join ',
  'order by',
  'pyodbc',
  'psycopg',
  'mysql.connector',
  'sqlalchemy',
  'sqlalche.me',
  'operationalerror',
  'programmingerror',
  'traceback',
  'odbc driver',
  'sql server',
  'sqlstate',
  '[sql:',
  'parameters:',
  'background on this error',
  'no sql template',
  "doesn't exist",
  'does not exist',
];

const looksTechnical = (detail: string): boolean => {
  const text = detail.toLowerCase();
  // A long single-line blob is almost always a dumped statement/stack.
  if (detail.length > 220) {
    return true;
  }
  return TECHNICAL_MARKERS.some((marker) => text.includes(marker));
};

const messageForStatus = (status: number, subject: string): string => {
  if (status === 401 || status === 403) {
    return `You don't have permission to view ${subject}.`;
  }
  if (status === 404) {
    return `${subject} could not be found.`;
  }
  if (status === 429) {
    return `Too many requests. Please wait a moment and try again.`;
  }
  if (status === 502 || status === 503 || status === 504) {
    return `${subject} is temporarily unavailable because the data source didn't respond. Please try again in a moment.`;
  }
  if (status >= 500) {
    return `${subject} couldn't be loaded right now. Please try again, and contact support if it keeps happening.`;
  }
  return `${subject} couldn't be loaded. Please try again.`;
};

/**
 * @param status  HTTP status from the failed response
 * @param detail  The API's `detail` field, if any
 * @param subject What failed, in user terms (e.g. "The Vacancy Report")
 */
export const friendlyApiError = (status: number, detail: unknown, subject: string): string => {
  const raw = typeof detail === 'string' ? detail.trim() : '';
  if (raw && !looksTechnical(raw)) {
    return raw;
  }
  return messageForStatus(status, subject);
};
